import type { CreativeGrouping } from "@nexus/contracts"
import type { CardStat, MyBrandCreativeCardModel } from "./drilldown-card.types"
import { asDrilldownDotColor, resolveDupBadgeCount } from "./drilldown-card-format"
import { accountKpiKey, resolveCardKpiTrend } from "./drilldown-card-trend"
import type { MyBrandKpiCatalogEntry } from "./my-brand-kpi-catalog"
import { formatTableValue } from "./format-table-value"
import {
  buildAdLibUrl,
  buildDrilldownRowKey,
  daysBetweenYmd,
  extractNumber,
  extractString,
  linkableAdId,
} from "./drilldown-row"

const UNSPLIT_GROUP = "All"

/** BQ NULL stays `null` (a real parent group); an absent column means the segment is off. */
function toGroupValue(val: unknown): string | null {
  if (val === undefined) return UNSPLIT_GROUP
  if (val === null) return null
  return extractString(val)
}

function toCardGrouping(row: Record<string, unknown>): CreativeGrouping {
  return {
    groupby2: toGroupValue(row.Groupby2),
    groupby3: toGroupValue(row.Groupby3),
    groupingKey: toGroupValue(row.grouping_key),
  }
}

/**
 * Maps a raw My Brand BQ row into the redesigned card's view model. `kpiIds` is
 * the user's ordered `myBrandKpis` selection — index 0 renders "big" on the
 * card, the rest render "compact" (positional, per the product decision;
 * independent of which KPI is the active sort).
 */
export function mapMyBrandCardRow(
  row: Record<string, unknown>,
  kpiIds: string[],
  catalogById: Record<string, MyBrandKpiCatalogEntry> = {},
  index = 0,
  currencySymbol = "$",
): MyBrandCreativeCardModel {
  const hash = extractString(row.hash)
  const adId = extractString(row.ad_id)
  const resolvedAdId = linkableAdId(adId) ? adId : null
  const objectType = extractString(row.object_type)
  const minDate = extractString(row.min_date)
  const maxDate = extractString(row.max_date)
  const creatives = extractNumber(row.creatives) ?? 0
  const grouping = toCardGrouping(row)

  const kpis: CardStat[] = kpiIds.map((id) => {
    const entry = catalogById[id]
    const value = extractNumber(row[id])
    const trend = resolveCardKpiTrend(id, value, extractNumber(row[accountKpiKey(id)]), {
      polarity: entry?.polarity,
      hasSumFormula: entry?.hasSumFormula,
    })
    return {
      label: entry?.label ?? id,
      value: formatTableValue(value, entry?.format ?? "number", currencySymbol, entry?.decimals ?? 2),
      dotColor: asDrilldownDotColor(entry?.colorVar ?? ""),
      ...(trend ?? {}),
    }
  })

  return {
    id: buildDrilldownRowKey(row, index),
    hash,
    // My Brand never returns carousel object types (confirmed: no included_hashes/
    // _types on DrilldownMyBrandRow) — any non-"image" type plays as a single video.
    isVideo: objectType.toLowerCase() !== "image",
    objectTypeLabel: objectType,
    thumbnailUrl: extractString(row.thumbnail_url),
    assetUrl: extractString(row.creative_url),
    adLibUrl: buildAdLibUrl(resolvedAdId ?? "", extractString(row.source)),
    dupCount: resolveDupBadgeCount(creatives),
    adsCount: extractNumber(row.ads) ?? 0,
    daysActive: daysBetweenYmd(minDate, maxDate),
    minDate,
    maxDate,
    kpis,
    adId: resolvedAdId,
    grouping,
  }
}
